import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import MeetingCard from '../components/MyMeetings/MeetingCard';
import ScopeToggle from '../components/MyMeetings/ScopeToggle';
import {
  PageWrapper, PageHeader, PageTitleGroup, PageTitle, PageSubtitle,
  HeaderControls, ErrorBanner, EmptyState, EmptyStateTitle, EmptyStateCode,
  MeetingsList, FilterBar, FilterPresets, FilterPresetButton, SearchInput, ResultCount,
  DateRangeGroup, DateRangeLabel, DateInput, ClearDateButton,
  PrivacyNotice, PrivacyNoticeIcon, ConsentToggleControl, ConsentToggleSwitch
} from './MyMeetingsPage.styles';

const DAYS_PRESETS = [
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: 'All time', value: 'all' },
];

const formatDaysLabel = (days) => {
  if (days === 'all') return 'All time';
  const n = parseInt(days, 10);
  return `Last ${n} day${n === 1 ? '' : 's'}`;
};

const formatRangeLabel = (from, to) => {
  if (from && to) return `${from} → ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Through ${to}`;
  return 'Custom range';
};

/**
 * Decide whether the current user can see a "team" scope toggle.
 * Mirrors getVisibleUserIds() in backend/murphy/meetings/get-mine.js,
 * which in turn mirrors NewAssessmentsReportPage's visibility rules.
 */
const userHasTeamScope = (currentUser, companyUsers) => {
  if (!currentUser || !companyUsers) return false;
  const userType = currentUser.userType;
  if (userType === 'admin' || userType === 'Company Admin' || userType === 'Executive') {
    return companyUsers.length > 0;
  }
  if (userType === 'Senior Manager') {
    return companyUsers.some(u =>
      u.reportsToId === currentUser.userId &&
      (u.userType === 'Sales Manager' || u.userType === 'Sales Member')
    );
  }
  if (userType === 'Sales Manager') {
    return companyUsers.some(u => u.reportsToId === currentUser.userId);
  }
  return false;
};

const MyMeetingsPage = observer(({ usersStore, companyStore, myMeetingsStore }) => {
  const location = useLocation();
  const companyId = companyStore?.company?.id;

  // Load on mount + when company changes
  useEffect(() => {
    if (companyId) {
      myMeetingsStore.loadMeetings(companyId).catch(() => {});
    }
    return () => {
      myMeetingsStore._stopPolling && myMeetingsStore._stopPolling();
    };
  }, [companyId, myMeetingsStore]);

  // Reload when scope, days, or date range changes
  useEffect(() => {
    if (companyId) {
      myMeetingsStore.loadMeetings(companyId, {
        scope: myMeetingsStore.scope,
        days: myMeetingsStore.days,
        dateFrom: myMeetingsStore.dateFrom,
        dateTo: myMeetingsStore.dateTo
      }).catch(() => {});
    }
  }, [
    myMeetingsStore.scope, myMeetingsStore.days,
    myMeetingsStore.dateFrom, myMeetingsStore.dateTo,
    companyId, myMeetingsStore
  ]);

  const hasTeamScope = useMemo(
    () => userHasTeamScope(usersStore?.currentUser, usersStore?.companyUsers),
    [usersStore?.currentUser, usersStore?.companyUsers]
  );

  // Company Admins (and Multiply admins) may switch the in-meeting recording
  // notice + opt-out on/off for their company. Wording stays Multiply-managed;
  // the backend enforces the same split (patchHandler consent-toggle carve-out).
  const currentUser = usersStore?.currentUser;
  const canToggleConsentNotice = !!currentUser &&
    (currentUser.userType === 'admin' || (currentUser.userType === 'Company Admin' && !currentUser.isReadOnly));
  const consentNoticeEnabled = companyStore?.company?.liveMeetingConsentNotice?.enabled === true;
  const [consentSaving, setConsentSaving] = React.useState(false);
  const onConsentToggle = async () => {
    if (consentSaving || !companyStore?.company) return;
    setConsentSaving(true);
    try {
      await companyStore.company.updateConsentNoticeEnabled(!consentNoticeEnabled);
    } catch (err) {
      console.error('Failed to toggle recording notice:', err);
    } finally {
      setConsentSaving(false);
    }
  };

  const onScopeChange = (newScope) => {
    if (newScope === 'team' && !hasTeamScope) return;
    myMeetingsStore.setScope(newScope);
  };

  const onDaysChange = (days) => {
    myMeetingsStore.setDays(days);
  };

  const allMeetings = myMeetingsStore.meetings;
  const meetings = myMeetingsStore.filteredMeetings;
  const isFilteredBySearch = myMeetingsStore.searchQuery.length > 0;
  const isInitialLoading = myMeetingsStore.isForegroundFetch && allMeetings.length === 0;

  // Optional deep-link: /live-meetings#meeting-{botId} expands that card.
  const expandBotId = useMemo(() => {
    const hash = location.hash || '';
    if (hash.startsWith('#meeting-')) return hash.slice('#meeting-'.length);
    return null;
  }, [location.hash]);

  return (
    <PageWrapper data-tour="my-meetings-page">
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Live Meetings</PageTitle>
          <PageSubtitle>
            {myMeetingsStore.scope === 'team' ? "Your team's live meetings" : 'Your live meetings'}
            {' · '}
            {myMeetingsStore.hasCustomDateRange
              ? formatRangeLabel(myMeetingsStore.dateFrom, myMeetingsStore.dateTo)
              : formatDaysLabel(myMeetingsStore.days)}
          </PageSubtitle>
        </PageTitleGroup>
        <HeaderControls>
          {hasTeamScope && (
            <ScopeToggle
              scope={myMeetingsStore.scope}
              onChange={onScopeChange}
              disabled={myMeetingsStore.isLoading}
            />
          )}
        </HeaderControls>
      </PageHeader>

      <PrivacyNotice>
        <PrivacyNoticeIcon aria-hidden="true">{'ℹ️'}</PrivacyNoticeIcon>
        <span>
          <strong>A note on privacy.</strong> Your data is secure. Murphy never stores your recordings and never uses your conversations to train models. Even so, only invite him to sales calls: prospecting, discovery, demos. If a meeting turns to PII, HR, finance, or anything confidential, just remove <code>murphy@askmultiply.com</code> from the participants and he'll drop off automatically.
        </span>
      </PrivacyNotice>

      {canToggleConsentNotice && (
        <PrivacyNotice style={{ alignItems: 'center' }}>
          <PrivacyNoticeIcon aria-hidden="true">{'📢'}</PrivacyNoticeIcon>
          <span style={{ flex: 1 }}>
            <strong>Recording notice.</strong> Murphy posts a recording notice in your live meetings' chat and leaves (deleting the recording) if anyone types "opt out".
          </span>
          <ConsentToggleControl>
            {consentNoticeEnabled ? 'On' : 'Off'}
            <ConsentToggleSwitch>
              <input
                type="checkbox"
                checked={consentNoticeEnabled}
                disabled={consentSaving}
                onChange={onConsentToggle}
              />
              <span />
            </ConsentToggleSwitch>
          </ConsentToggleControl>
        </PrivacyNotice>
      )}

      <FilterBar>
        <FilterPresets>
          {DAYS_PRESETS.map(p => (
            <FilterPresetButton
              key={p.value}
              $active={!myMeetingsStore.hasCustomDateRange && myMeetingsStore.days === p.value}
              onClick={() => onDaysChange(p.value)}
              disabled={myMeetingsStore.isLoading}
              type="button"
            >
              {p.label}
            </FilterPresetButton>
          ))}
        </FilterPresets>
        <DateRangeGroup>
          <DateRangeLabel>From</DateRangeLabel>
          <DateInput
            type="date"
            value={myMeetingsStore.dateFrom}
            max={myMeetingsStore.dateTo || undefined}
            onChange={(e) => myMeetingsStore.setDateFrom(e.target.value)}
          />
          <DateRangeLabel>To</DateRangeLabel>
          <DateInput
            type="date"
            value={myMeetingsStore.dateTo}
            min={myMeetingsStore.dateFrom || undefined}
            onChange={(e) => myMeetingsStore.setDateTo(e.target.value)}
          />
          {myMeetingsStore.hasCustomDateRange && (
            <ClearDateButton
              onClick={() => myMeetingsStore.clearDateRange()}
              type="button"
              title="Clear date range"
            >
              {'✕'} Clear
            </ClearDateButton>
          )}
        </DateRangeGroup>
        <SearchInput
          type="text"
          placeholder="Search by meeting title or participant…"
          value={myMeetingsStore.searchQuery}
          onChange={(e) => myMeetingsStore.setSearchQuery(e.target.value)}
        />
        {allMeetings.length > 0 && (
          <ResultCount>
            {isFilteredBySearch
              ? `${meetings.length} of ${allMeetings.length} meeting${allMeetings.length === 1 ? '' : 's'}`
              : `${allMeetings.length} meeting${allMeetings.length === 1 ? '' : 's'}`}
          </ResultCount>
        )}
      </FilterBar>

      {myMeetingsStore.error && (
        <ErrorBanner>Couldn't load meetings: {myMeetingsStore.error}</ErrorBanner>
      )}

      {isInitialLoading ? (
        <EmptyState>Loading…</EmptyState>
      ) : meetings.length === 0 ? (
        <EmptyState>
          {isFilteredBySearch ? (
            <>
              <EmptyStateTitle>No meetings match your search</EmptyStateTitle>
              Try clearing the search or expanding the date range.
            </>
          ) : myMeetingsStore.scope === 'team' ? (
            <>
              <EmptyStateTitle>No meetings yet for your team</EmptyStateTitle>
              When your reports invite Murphy to their calls, they'll appear here.
            </>
          ) : (
            <>
              <EmptyStateTitle>You haven't had any Murphy-recorded meetings yet</EmptyStateTitle>
              Invite <EmptyStateCode>murphy@askmultiply.com</EmptyStateCode> to your next sales call's calendar event to get started.
            </>
          )}
        </EmptyState>
      ) : (
        <MeetingsList
          // Dim the list during a foreground refetch (e.g. scope toggle, date
          // range change). The fetch flips `isLoading`; we keep showing the
          // old list to avoid a layout collapse but make it clear something
          // is in flight.
          style={{
            opacity: myMeetingsStore.isForegroundFetch ? 0.5 : 1,
            transition: 'opacity 150ms ease',
            pointerEvents: myMeetingsStore.isForegroundFetch ? 'none' : 'auto',
          }}
        >
          {meetings.map(meeting => (
            <MeetingCard
              key={meeting.botId}
              meeting={meeting}
              myMeetingsStore={myMeetingsStore}
              companyStore={companyStore}
              usersStore={usersStore}
              scope={myMeetingsStore.scope}
              defaultExpanded={meeting.botId === expandBotId}
            />
          ))}
        </MeetingsList>
      )}
    </PageWrapper>
  );
});

export default MyMeetingsPage;
